// Sentence segmentation for zh source text. Pure string logic — the engine
// caps a source row at SRC_CAP=320 tokens (~1 token per CJK char), so rows
// are pre-chunked at MAX_SENT_CHARS chars to stay clear of truncation.
// 200, not 256: vi output runs ~0.96 tokens/source-char against the
// DECODE_CAP=224 output ring, so a 256-char row (a single long sentence —
// packSentences never splits sentences) needs ~246 output tokens and gets
// its tail cut. 200-char rows stay clear (~215 with margin).
export const MAX_SENT_CHARS = 200;

const TERMINATOR = /[。｡．！!？?；;…]/;
const CLOSER = /[”’』」）〉》"')\]]/;
const COMMA = /[，、,]/;

function splitSentences(para) {
  const out = [];
  let start = 0;
  for (let i = 0; i < para.length; i++) {
    if (!TERMINATOR.test(para[i])) continue;
    let j = i + 1;
    while (j < para.length && (TERMINATOR.test(para[j]) || CLOSER.test(para[j]))) j++;
    const s = para.slice(start, j).trim();
    if (s) out.push(s);
    start = j;
    i = j - 1;
  }
  const tail = para.slice(start).trim();
  if (tail) out.push(tail);
  return out;
}

// Split a row into pieces of ≤maxChars, cutting at the comma nearest the
// middle (mid-clause cuts read better than hard char cuts) and falling back
// to a hard cut when the row has no comma. Exported for the engine's
// truncation rescue: a row whose TOKEN count blew past SRC_CAP (byte-fallback
// emoji, Ext-B Han, dense Latin) re-splits at half its char length and
// re-decodes — same comma-aware cutting the chunker itself uses.
export function chunkTo(sent, maxChars) {
  if (sent.length <= maxChars) return [sent];
  const mid = sent.length / 2;
  let best = -1;
  for (let i = 0; i < sent.length - 1; i++) {
    if (COMMA.test(sent[i]) && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
  }
  const cut = best >= 0 ? best + 1 : maxChars;
  const head = sent.slice(0, cut).trim();
  const rest = sent.slice(cut).trim();
  return [
    ...(head ? chunkTo(head, maxChars) : []),
    ...(rest ? chunkTo(rest, maxChars) : []),
  ];
}

function chunkLong(sent) {
  return chunkTo(sent, MAX_SENT_CHARS);
}

// segmentSource(text) → {sentences, paras}: sentences is the flat row list to
// translate (order preserved); paras[i] is how many of those rows belong to
// paragraph i (empty paragraph → 0), so the translation can be reassembled
// with the original line structure.
export function segmentSource(text) {
  const sentences = [];
  const paras = [];
  for (const para of text.split('\n')) {
    let count = 0;
    for (const s of splitSentences(para)) {
      for (const piece of chunkLong(s)) {
        sentences.push(piece);
        count++;
      }
    }
    paras.push(count);
  }
  return { sentences, paras };
}

// Pack consecutive sentences of the SAME paragraph into rows of ≤maxChars —
// file-mode throughput: ~5× fewer tokenizer calls and longer decode rows
// that amortize like the chapter/file bench presets. CJK sentences carry
// their terminators, so plain concatenation is lossless; paragraph
// boundaries are never crossed.
//
// 200, not 224: measured vi output runs ~0.96 tokens per source char, and
// the decode ring holds DECODE_CAP=224 steps — 200·0.96·1.12-margin ≈ 215
// keeps full rows clear of a forced-EOS tail cut.
export function packSentences(sentences, paras, maxChars = 200) {
  const rows = [];
  const rowParas = [];
  let k = 0;
  for (const count of paras) {
    let cur = '';
    let n = 0;
    for (let i = 0; i < count; i++) {
      const s = sentences[k + i];
      if (cur && cur.length + s.length > maxChars) {
        rows.push(cur);
        n++;
        cur = '';
      }
      cur += s;
    }
    if (cur) {
      rows.push(cur);
      n++;
    }
    k += count;
    rowParas.push(n);
  }
  return { rows, rowParas };
}

export function assembleTranslation(paras, translated) {
  const lines = [];
  let k = 0;
  for (const count of paras) {
    lines.push(translated.slice(k, k + count).join(' ').trim());
    k += count;
  }
  return lines.join('\n');
}
